/**
 * Portfolio: Balances, Positions, Open orders, Order history, Trades, Settled (#788) for the viewed account
 * (connected wallet or ?ghost= address). The last three panels live in web/src/account/orders/
 * (built separately); until they land, the slots below render placeholders.
 */
import { useState, type FormEvent, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import BalancesPanel from '../account/BalancesPanel';
import PositionsPanel from '../account/PositionsPanel';
import { OpenOrdersSlot, OrderHistorySlot, TradesSlot } from '../account/OrderSlots';
import { SettledPanel } from '../settlement/ui';
import { shortAddress } from '../wallet/address';
import { useGhost, useViewedAccount } from '../wallet/context';
import type { PlainKey } from '../i18n/i18n';
import { useT } from '../i18n/useT';

const PORTFOLIO_TABS = [
  { id: 'balances', label: 'portfolio.tab.balances' },
  { id: 'positions', label: 'portfolio.tab.positions' },
  { id: 'orders', label: 'portfolio.tab.orders' },
  { id: 'history', label: 'portfolio.tab.history' },
  { id: 'trades', label: 'portfolio.tab.trades' },
  { id: 'settled', label: 'portfolio.tab.settled' },
] as const satisfies readonly { id: string; label: PlainKey }[];
type TabId = (typeof PORTFOLIO_TABS)[number]['id'];

function isTab(v: string | null): v is TabId {
  return PORTFOLIO_TABS.some((t) => t.id === v);
}

function NoAccount() {
  const { enterGhost, ghostError } = useGhost();
  const t = useT();
  const [input, setInput] = useState('');
  const [bad, setBad] = useState(false);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    setBad(!enterGhost(input.trim()));
  };
  return (
    <div className="card space-y-3 p-6 text-sm" data-testid="portfolio-empty">
      <p className="text-muted">{t('portfolio.empty')}</p>
      <form onSubmit={submit} className="flex flex-col gap-2 sm:flex-row">
        <input
          aria-label={t('portfolio.ghost_input')}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={t('portfolio.ghost_placeholder')}
          className="num min-w-0 flex-1 rounded-md border border-border bg-surface-2 px-3 py-2"
        />
        <button type="submit" className="rounded-md bg-accent/20 px-3 py-2 font-medium text-accent hover:bg-accent/30">
          {t('portfolio.watch')}
        </button>
      </form>
      {(bad || ghostError) && <p className="text-danger">{ghostError ?? t('portfolio.not_valid')}</p>}
    </div>
  );
}

function TabPanel({ id, active, children }: { id: TabId; active: boolean; children: ReactNode }) {
  if (!active) return null;
  return (
    <div role="tabpanel" id={`portfolio-panel-${id}`} aria-labelledby={`portfolio-tab-${id}`}>
      {children}
    </div>
  );
}

export default function PortfolioPage() {
  const { address, mode } = useViewedAccount();
  const t = useT();
  const [params, setParams] = useSearchParams();
  const raw = params.get('tab');
  const tab: TabId = isTab(raw) ? raw : 'balances';
  const select = (id: TabId) => {
    const next = new URLSearchParams(params);
    if (id === 'balances') next.delete('tab');
    else next.set('tab', id);
    setParams(next, { replace: true });
  };

  return (
    <section className="space-y-4" data-tour="portfolio">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h1 className="text-xl font-semibold sm:text-2xl">{t('nav.portfolio')}</h1>
        {address && (
          <span className="num text-sm text-muted" data-testid="portfolio-account">
            {mode === 'ghost' ? t('portfolio.watching', { address: shortAddress(address) }) : shortAddress(address)}
          </span>
        )}
      </div>

      {!address ? (
        <NoAccount />
      ) : (
        <>
          <div role="tablist" aria-label={t('portfolio.sections')} className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
            <div className="flex w-max gap-1 rounded-lg bg-surface-2 p-1">
              {PORTFOLIO_TABS.map((pt) => (
                <button
                  key={pt.id}
                  type="button"
                  role="tab"
                  id={`portfolio-tab-${pt.id}`}
                  aria-selected={tab === pt.id}
                  aria-controls={`portfolio-panel-${pt.id}`}
                  onClick={() => select(pt.id)}
                  className={`whitespace-nowrap rounded-md px-3 py-1.5 text-sm ${
                    tab === pt.id ? 'bg-surface font-medium text-fg' : 'text-muted hover:text-fg'
                  }`}
                >
                  {t(pt.label)}
                </button>
              ))}
            </div>
          </div>
          {/* key={address}: switching account remounts panels so no stale rows flash. */}
          <div key={address}>
            <TabPanel id="balances" active={tab === 'balances'}>
              <BalancesPanel address={address} />
            </TabPanel>
            <TabPanel id="positions" active={tab === 'positions'}>
              <PositionsPanel address={address} />
            </TabPanel>
            <TabPanel id="orders" active={tab === 'orders'}>
              <OpenOrdersSlot address={address} />
            </TabPanel>
            <TabPanel id="history" active={tab === 'history'}>
              <OrderHistorySlot address={address} />
            </TabPanel>
            <TabPanel id="trades" active={tab === 'trades'}>
              <TradesSlot address={address} />
            </TabPanel>
            <TabPanel id="settled" active={tab === 'settled'}>
              <SettledPanel address={address} />
            </TabPanel>
          </div>
        </>
      )}
    </section>
  );
}
